import { useEffect, useRef, useState } from "react";
import { Download, Share2, X, Trophy } from "lucide-react";

/**
 * Renders a 1080×1080 winner-card poster on an off-screen canvas
 * and exposes Download PNG + Share (Web Share API / WhatsApp fallback) buttons.
 *
 * Props:
 *   winner: { name, picture?, score }
 *   date: "YYYY-MM-DD"
 *   dayLabel: "Thu, 16 Jul 2026"
 *   onClose: () => void
 *   shareUrl?: string  // link included in share message (defaults to site root)
 *   rank?: 1 | 2 | 3     // if provided, uses "TOP {rank} · LIVE" instead of DAILY WINNER
 *   variant?: 'winner' | 'live' // 'live' = challenge-a-friend copy, no ₹500 claim
 */
export default function WinnerCard({ winner, date, dayLabel, onClose, shareUrl, rank, variant = "winner" }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !winner) return;
    const W = 1080;
    const H = 1080;
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d");

    // === Background: solid black + subtle grid ===
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(0, 240, 255, 0.08)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
    }
    for (let y = 0; y <= H; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
    }

    // === Top strip: yellow arcade bar ===
    ctx.fillStyle = "#EAEB18";
    ctx.fillRect(0, 0, W, 90);
    ctx.fillStyle = "#050505";
    ctx.font = "800 42px Arial Black, Impact, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("★ DAREDINO", 48, 45);
    ctx.font = "600 24px Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(dayLabel || date, W - 48, 45);

    // === Central WINNER card box ===
    const cardX = 60;
    const cardY = 170;
    const cardW = W - 120;
    const cardH = 780;
    ctx.fillStyle = "#0B0C10";
    ctx.fillRect(cardX, cardY, cardW, cardH);
    ctx.strokeStyle = "#EAEB18";
    ctx.lineWidth = 4;
    ctx.strokeRect(cardX, cardY, cardW, cardH);
    // Corner accents
    ctx.strokeStyle = "#FF007F";
    ctx.lineWidth = 4;
    const corner = 40;
    // TL
    ctx.beginPath(); ctx.moveTo(cardX, cardY + corner); ctx.lineTo(cardX, cardY); ctx.lineTo(cardX + corner, cardY); ctx.stroke();
    // TR
    ctx.beginPath(); ctx.moveTo(cardX + cardW - corner, cardY); ctx.lineTo(cardX + cardW, cardY); ctx.lineTo(cardX + cardW, cardY + corner); ctx.stroke();
    // BR
    ctx.beginPath(); ctx.moveTo(cardX + cardW, cardY + cardH - corner); ctx.lineTo(cardX + cardW, cardY + cardH); ctx.lineTo(cardX + cardW - corner, cardY + cardH); ctx.stroke();
    // BL
    ctx.beginPath(); ctx.moveTo(cardX + corner, cardY + cardH); ctx.lineTo(cardX, cardY + cardH); ctx.lineTo(cardX, cardY + cardH - corner); ctx.stroke();

    // === "DAILY WINNER" or "TOP N · LIVE" pill ===
    const pillLabel = variant === "live" && rank
      ? `\u2605 TOP ${rank} \u00B7 LIVE`
      : "\u2605 DAILY WINNER";
    ctx.fillStyle = "#FF007F";
    ctx.fillRect(cardX + 40, cardY + 40, 260, 44);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 18px Arial Black, Impact, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(pillLabel, cardX + 60, cardY + 62);

    // === ₹500 PRIZE / CHALLENGE badge (top-right) ===
    const badgeX = cardX + cardW - 260;
    const badgeY = cardY + 40;
    ctx.fillStyle = "#EAEB18";
    ctx.fillRect(badgeX, badgeY, 220, 44);
    ctx.fillStyle = "#050505";
    ctx.font = "800 20px Arial Black, sans-serif";
    ctx.textAlign = "center";
    const badgeLabel = variant === "live"
      ? "BEAT MY SCORE"
      : "DAILY 1ST PRIZE";
    ctx.fillText(badgeLabel, badgeX + 110, badgeY + 22);

    // === Trophy icon ===
    ctx.fillStyle = "#EAEB18";
    ctx.font = "180px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🏆", W / 2, cardY + 250);

    // === Player name ===
    const displayName = (winner.name || "Player").slice(0, 24);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 68px Arial Black, Impact, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(displayName.toUpperCase(), W / 2, cardY + 400);

    // === "SCORED" label ===
    ctx.fillStyle = "#94A3B8";
    ctx.font = "600 22px Arial, sans-serif";
    ctx.fillText("SURVIVED WITH A SCORE OF", W / 2, cardY + 470);

    // === Big score ===
    ctx.fillStyle = "#00F0FF";
    ctx.font = "900 180px 'Courier New', monospace";
    ctx.fillText(String(winner.score).padStart(5, "0"), W / 2, cardY + 590);

    // === Divider line ===
    ctx.strokeStyle = "#00F0FF";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cardX + 60, cardY + 700);
    ctx.lineTo(cardX + cardW - 60, cardY + 700);
    ctx.stroke();

    // === Bottom CTA ===
    ctx.fillStyle = "#EAEB18";
    ctx.font = "800 26px Arial Black, sans-serif";
    const cta = variant === "live"
      ? "CAN YOU BEAT ME? PLAY DAREDINO NOW"
      : "PLAY DAREDINO \u2014 WIN CASH DAILY";
    ctx.fillText(cta, W / 2, cardY + 745);

    // === Footer strip ===
    ctx.fillStyle = "#FF007F";
    ctx.fillRect(0, H - 90, W, 90);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 32px Arial Black, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("CHROME DINO GAME · REAL CASH", W / 2, H - 45);

    // Cache dataUrl
    setDataUrl(c.toDataURL("image/png"));
  }, [winner, date, dayLabel, rank, variant]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `DareDino-Winner-${date}-${(winner.name || "player").replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const shareText = variant === "live"
    ? `\uD83C\uDFC6 I'm ranked #${rank || "top"} on DareDino today with a score of ${winner.score}! Think you can beat me?\n\nPlay the Chrome Dino Game & win real cash daily \u2192 ${shareUrl || window.location.origin}`
    : `\uD83C\uDFC6 ${winner.name} won today's daily cash prize on DareDino with a score of ${winner.score}!\n\nPlay the Chrome Dino Game & win real cash daily \u2192 ${shareUrl || window.location.origin}`;

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareNative = async () => {
    if (!navigator.share || !dataUrl) return shareWhatsApp();
    try {
      // Convert dataURL → File so image is shared, not just text
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `winner-${date}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "DareDino Winner", text: shareText });
        return;
      }
      await navigator.share({ title: "DareDino Winner", text: shareText });
    } catch {
      shareWhatsApp();
    }
  };

  if (!winner) return null;

  return (
    <div
      data-testid="winner-card-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border-2 border-[#EAEB18] bg-[#0B0C10] p-4 sm:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy size={16} strokeWidth={2.5} className="text-[#EAEB18]" />
            <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
              Winner Card
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="winner-card-close"
            className="text-[#FF007F] hover:text-[#EAEB18]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Preview — the canvas is 1080×1080 but we scale it down for display */}
        <div className="bg-[#050505] border border-[#EAEB18]/40 mb-4 overflow-hidden">
          <canvas
            ref={canvasRef}
            data-testid="winner-card-canvas"
            className="w-full h-auto block"
            style={{ imageRendering: "auto" }}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="winner-card-download"
            onClick={download}
            disabled={!dataUrl}
            className="inline-flex items-center justify-center gap-2 bg-[#EAEB18] text-black font-heading font-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors disabled:opacity-50"
          >
            <Download size={14} strokeWidth={2.5} /> PNG
          </button>
          <button
            data-testid="winner-card-share"
            onClick={shareNative}
            disabled={!dataUrl}
            className="inline-flex items-center justify-center gap-2 bg-[#39FF14] text-black font-heading font-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors disabled:opacity-50"
          >
            <Share2 size={14} strokeWidth={2.5} /> SHARE
          </button>
        </div>
        <div className="text-[10px] text-[#94A3B8] mt-3 text-center leading-relaxed">
          Share button uses your device&apos;s native share sheet (WhatsApp,
          Twitter, Instagram etc.). On desktop it falls back to WhatsApp Web.
        </div>
      </div>
    </div>
  );
}
